import React, { useState, useEffect } from 'react';
import { FaArrowLeft, FaCheck, FaEnvelope, FaPhone, FaMapMarker, FaHome, FaHeart } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';

const ContactInformation = ({ formData = {}, onSubmit, onPrevious, loading, userId }) => {
    const [localData, setLocalData] = useState({
        country: formData?.country || '153', // Default country ID 153 (Nepal)
        state: formData?.state || '5092', // Default state ID 2
        district: formData?.district || '',
        city: formData?.city || '', // Will store city name as string
        residence_status: formData?.residence_status || '',
        email: formData?.email || '',
        contact: formData?.contact || '',
        phone_code: formData?.phone_code || '+977' // Default phone code for Nepal
    });
    
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [countryCodes, setCountryCodes] = useState([]); // For phone country codes
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    // Fetch countries on component mount
    useEffect(() => {
        fetchCountries();
        fetchCountryCodes(); // Fetch country codes for phone numbers
    }, []);

    // Fetch states when country changes (including initial load)
    useEffect(() => {
        const loadStates = async () => {
            if (localData.country) {
                setLoadingStates(true);
                try {
                    const response = await axios.get(`https://app.bihebari.com.np/api/locations/states/${localData.country}`);
                    if (response.data.success) {
                        setStates(response.data.data);
                        
                        // On initial load, set default state if not already set
                        if (isInitialLoad && !formData?.state && localData.country === '153') {
                            setLocalData(prev => ({ ...prev, state: '2' }));
                        }
                        
                        // Reset state and city only if country changes (not on initial load)
                        if (!isInitialLoad) {
                            setLocalData(prev => ({ ...prev, state: '', city: '' }));
                        }
                    } else {
                        setStates([]);
                    }
                } catch (error) {
                    console.error('Error fetching states:', error);
                    toast.error('Error loading states');
                } finally {
                    setLoadingStates(false);
                }
            } else {
                setStates([]);
                setCities([]);
            }
        };
        loadStates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localData.country, isInitialLoad]);

    // Fetch cities when state changes (including initial load)
    useEffect(() => {
        const loadCities = async () => {
            if (localData.state) {
                setLoadingCities(true);
                try {
                    const response = await axios.get(`https://app.bihebari.com.np/api/locations/cities/${localData.state}`);
                    if (response.data.success) {
                        setCities(response.data.data);
                        
                        // Set default city name instead of ID
                        if (isInitialLoad && !formData?.city && localData.state === '2') {
                            const defaultCity = response.data.data.find(city => city.id === 19);
                            if (defaultCity) {
                                setLocalData(prev => ({ ...prev, city: defaultCity.name }));
                            }
                            setIsInitialLoad(false);
                        }
                        
                        // Reset city when state changes (not on initial load)
                        if (!isInitialLoad) {
                            setLocalData(prev => ({ ...prev, city: '' }));
                        }
                    } else {
                        setCities([]);
                    }
                } catch (error) {
                    console.error('Error fetching cities:', error);
                    toast.error('Error loading cities');
                } finally {
                    setLoadingCities(false);
                }
            } else {
                setCities([]);
            }
        };
        loadCities();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localData.state, isInitialLoad]);

    const fetchCountries = async () => {
        try {
            const response = await axios.get('https://app.bihebari.com.np/api/locations/countries');
            if (response.data.success) {
                setCountries(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching countries:', error);
            toast.error('Error loading countries');
        }
    };

    // Fetch country codes for phone numbers
    const fetchCountryCodes = async () => {
        try {
            // You can either fetch from API or use a predefined list
            const response = await axios.get('https://restcountries.com/v3.1/all?fields=name,idd');
            if (response.data) {
                const codes = response.data
                    .filter(country => country.idd && country.idd.root)
                    .map(country => ({
                        name: country.name.common,
                        code: country.idd.root + (country.idd.suffixes ? country.idd.suffixes[0] : ''),
                        dial_code: country.idd.root + (country.idd.suffixes ? country.idd.suffixes[0] : '')
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name));
                setCountryCodes(codes);
            }
        } catch (error) {
            console.error('Error fetching country codes:', error);
            // Fallback to common country codes
            setCountryCodes([
                { name: 'Afghanistan', code: '+93', dial_code: '+93' },
                { name: 'Australia', code: '+61', dial_code: '+61' },
                { name: 'Bangladesh', code: '+880', dial_code: '+880' },
                { name: 'Canada', code: '+1', dial_code: '+1' },
                { name: 'China', code: '+86', dial_code: '+86' },
                { name: 'India', code: '+91', dial_code: '+91' },
                { name: 'Japan', code: '+81', dial_code: '+81' },
                { name: 'Nepal', code: '+977', dial_code: '+977' },
                { name: 'Pakistan', code: '+92', dial_code: '+92' },
                { name: 'Sri Lanka', code: '+94', dial_code: '+94' },
                { name: 'United Kingdom', code: '+44', dial_code: '+44' },
                { name: 'United States', code: '+1', dial_code: '+1' },
            ]);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'city') {
            // Find the selected city object to get the name
            const selectedCity = cities.find(city => city.id === parseInt(value));
            if (selectedCity) {
                // Store the city NAME as string, not the ID
                setLocalData({ ...localData, city: selectedCity.name });
            } else {
                setLocalData({ ...localData, city: '' });
            }
        } else {
            setLocalData({ ...localData, [name]: value });
        }
        
        if (name === 'country') {
            setLocalData(prev => ({ ...prev, state: '', city: '' }));
        }
        if (name === 'state') {
            setLocalData(prev => ({ ...prev, city: '' }));
        }
        
        setTouched({ ...touched, [name]: true });
        
        if (errors[name]) {
            setErrors({ ...errors, [name]: null });
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched({ ...touched, [name]: true });
        validateField(name, localData[name]);
    };

    const validateField = (fieldName, value) => {
        let error = '';
        
        switch (fieldName) {
            case 'country':
                if (!value || value === '') error = 'Country is required';
                break;
            case 'state':
                if (!value || value === '') error = 'State is required';
                break;
            case 'district':
                if (!value || value.trim() === '') {
                    error = 'District is required';
                } else if (value.length < 2) {
                    error = 'District must be at least 2 characters';
                }
                break;
            case 'city':
                if (!value || value.trim() === '') error = 'City is required';
                break;
            case 'residence_status':
                if (!value || value === '') error = 'Residence status is required';
                break;
            case 'email':
                if (!value || value.trim() === '') {
                    error = 'Email is required';
                } else {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(value)) {
                        error = 'Please enter a valid email address';
                    }
                }
                break;
            case 'contact':
                if (!value || value.trim() === '') {
                    error = 'Mobile number is required';
                } else {
                    // Remove any non-digit characters for validation
                    const cleanNumber = value.replace(/\D/g, '');
                    if (cleanNumber.length < 6 || cleanNumber.length > 15) {
                        error = 'Mobile number must be between 6-15 digits';
                    }
                }
                break;
            case 'phone_code':
                if (!value || value === '') error = 'Country code is required';
                break;
            default:
                break;
        }
        
        setErrors(prev => ({ ...prev, [fieldName]: error }));
        return error === '';
    };

    const validateForm = () => {
        const fieldsToValidate = [
            'country', 'state', 'district', 'city', 
            'residence_status', 'email', 'contact', 'phone_code'
        ];
        
        let isValid = true;
        const newErrors = {};
        
        fieldsToValidate.forEach(field => {
            const value = localData[field];
            let error = '';
            
            switch (field) {
                case 'country':
                    if (!value || value === '') error = 'Country is required';
                    break;
                case 'state':
                    if (!value || value === '') error = 'State is required';
                    break;
                case 'district':
                    if (!value || value.trim() === '') error = 'District is required';
                    break;
                case 'city':
                    if (!value || value.trim() === '') error = 'City is required';
                    break;
                case 'residence_status':
                    if (!value || value === '') error = 'Residence status is required';
                    break;
                case 'email':
                    if (!value || value.trim() === '') {
                        error = 'Email is required';
                    } else {
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (!emailRegex.test(value)) {
                            error = 'Please enter a valid email address';
                        }
                    }
                    break;
                case 'contact':
                    if (!value || value.trim() === '') {
                        error = 'Mobile number is required';
                    } else {
                        const cleanNumber = value.replace(/\D/g, '');
                        if (cleanNumber.length < 6 || cleanNumber.length > 15) {
                            error = 'Mobile number must be between 6-15 digits';
                        }
                    }
                    break;
                case 'phone_code':
                    if (!value || value === '') error = 'Country code is required';
                    break;
                default:
                    break;
            }
            
            if (error) {
                isValid = false;
                newErrors[field] = error;
            }
        });
        
        setErrors(newErrors);
        
        const allTouched = {};
        fieldsToValidate.forEach(field => {
            allTouched[field] = true;
        });
        setTouched(allTouched);
        
        return isValid;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            // Combine country code and mobile number
            const fullMobileNumber = localData.phone_code + localData.contact.replace(/\D/g, '');
            
            const submissionData = {
                user_id: userId,
                country: localData.country,
                state: localData.state,
                distric: localData.district || '',
                city: localData.city, // This is now the city NAME as string
                residence_status: localData.residence_status || '',
                email: localData.email || '',
                contact: fullMobileNumber, // Store full number with country code
                phone_code: localData.phone_code // Store phone code separately if needed
            };
            
            console.log('Submitting contact data (with city name as string):', submissionData);
            onSubmit(submissionData);
        } else {
            toast.error('Please fill all required fields correctly');
        }
    };

    // Get selected names for display
    const getSelectedCountryName = () => {
        const country = countries.find(c => Number(c.id) === Number(localData.country));
        return country ? country.name : '';
    };

    const getSelectedStateName = () => {
        const state = states.find(s => Number(s.id) === Number(localData.state));
        return state ? state.name : '';
    };

    // Get the current city ID for the dropdown (to show selected value)
    const getSelectedCityId = () => {
        if (!localData.city) return '';
        const city = cities.find(c => c.name === localData.city);
        return city ? city.id : '';
    };

    // Safely access formData properties with optional chaining
    const formDataName = formData?.name || 'Not provided';
    const formDataDob = formData?.dob || 'Not provided';
    const formDataGender = formData?.gender || 'Not provided';
    const formDataCaste = formData?.caste || 'Not provided';
    const formDataGotra = formData?.gotra || 'Not provided';
    const formDataMarital = formData?.marital || 'Not provided';

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex items-center space-x-2 mb-6">
                <div className="bg-gradient-to-r from-red-500 to-blue-500 p-2 rounded-full">
                    <FaMapMarker className="text-white text-xl" />
                </div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-red-600 to-blue-600 bg-clip-text text-transparent">
                    Contact Information
                </h2>
                <FaHeart className="text-red-400 text-lg animate-pulse" />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Country Dropdown */}
                <div>
                    <label className="form-label">
                        <FaMapMarker className="inline mr-2" />
                        Country <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="country"
                        value={localData.country}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input focus:ring-red-500 focus:border-red-500 ${errors.country && touched.country ? 'input-error' : ''}`}
                    >
                        <option value="">Select Country</option>
                        {countries.map((country) => (
                            <option key={country.id} value={country.id}>
                                {country.name}
                            </option>
                        ))}
                    </select>
                    {errors.country && touched.country && <p className="error-message">{errors.country}</p>}
                </div>
                
                {/* State Dropdown */}
                <div>
                    <label className="form-label">
                        State <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="state"
                        value={localData.state}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input focus:ring-red-500 focus:border-red-500 ${errors.state && touched.state ? 'input-error' : ''}`}
                        disabled={!localData.country || loadingStates}
                    >
                        <option value="">Select State</option>
                        {states.map((state) => (
                            <option key={state.id} value={state.id}>
                                {state.name}
                            </option>
                        ))}
                    </select>
                    {errors.state && touched.state && <p className="error-message">{errors.state}</p>}
                    {loadingStates && <p className="text-xs text-gray-500 mt-1">Loading states...</p>}
                    {!localData.country && <p className="text-xs text-gray-500 mt-1">Please select country first</p>}
                </div>
                
                {/* District - Text Input */}
                <div>
                    <label className="form-label">
                        District / City <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="district"
                        value={localData.district}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input focus:ring-red-500 focus:border-red-500 ${errors.district && touched.district ? 'input-error' : ''}`}
                        placeholder="Enter district name"
                    />
                    {errors.district && touched.district && <p className="error-message">{errors.district}</p>}
                    <p className="text-xs text-gray-500 mt-1">Enter your district name</p>
                </div>
                
                {/* City - Dropdown that saves name as string */}
                <div>
                    <label className="form-label">
                        City / District<span className="text-red-500">*</span>
                    </label>
                    <select
                        name="city"
                        value={getSelectedCityId()}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input focus:ring-red-500 focus:border-red-500 ${errors.city && touched.city ? 'input-error' : ''}`}
                        disabled={!localData.state || loadingCities}
                    >
                        <option value="">Select City</option>
                        {cities.map((city) => (
                            <option key={city.id} value={city.id}>
                                {city.name}
                            </option>
                        ))}
                    </select>
                    {errors.city && touched.city && <p className="error-message">{errors.city}</p>}
                    {loadingCities && <p className="text-xs text-gray-500 mt-1">Loading cities...</p>}
                    {!localData.state && localData.country && <p className="text-xs text-gray-500 mt-1">Please select state first</p>}
                    {!loadingCities && cities.length === 0 && localData.state && (
                        <p className="text-xs text-amber-600 mt-1">No cities available for selected state</p>
                    )}
                </div>
                
                {/* Residence Status */}
                <div>
                    <label className="form-label">
                        <FaHome className="inline mr-2" />
                        Residence Status <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="residence_status"
                        value={localData.residence_status}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input focus:ring-red-500 focus:border-red-500 ${errors.residence_status && touched.residence_status ? 'input-error' : ''}`}
                    >
                        <option value="">Select Residence Status</option>
                        <option value="citizenship">Citizenship</option>
                        <option value="tr">TR</option>
                        <option value="pr">PR</option>
                        <option value="other">Other</option>
                    </select>
                    {errors.residence_status && touched.residence_status && <p className="error-message">{errors.residence_status}</p>}
                </div>
                
                {/* Email */}
                <div>
                    <label className="form-label">
                        <FaEnvelope className="inline mr-2" />
                        Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="email"
                        name="email"
                        value={localData.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input focus:ring-red-500 focus:border-red-500 ${errors.email && touched.email ? 'input-error' : ''}`}
                        placeholder="example@email.com"
                    />
                    {errors.email && touched.email && <p className="error-message">{errors.email}</p>}
                </div>
                
                {/* Mobile Number with Country Code */}
                <div className="md:col-span-2">
                    <label className="form-label">
                        <FaPhone className="inline mr-2" />
                        Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                        {/* Country Code Dropdown */}
                        <div className="w-1/3">
                            <select
                                name="phone_code"
                                value={localData.phone_code}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`form-input focus:ring-red-500 focus:border-red-500 ${errors.phone_code && touched.phone_code ? 'input-error' : ''}`}
                            >
                                <option value="">Select Code</option>
                                {countryCodes.map((country) => (
                                    <option key={country.code} value={country.code}>
                                        {country.name} ({country.code})
                                    </option>
                                ))}
                            </select>
                            {errors.phone_code && touched.phone_code && <p className="error-message">{errors.phone_code}</p>}
                        </div>
                        
                        {/* Mobile Number Input */}
                        <div className="w-2/3">
                            <input
                                type="tel"
                                name="contact"
                                value={localData.contact}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`form-input focus:ring-red-500 focus:border-red-500 ${errors.contact && touched.contact ? 'input-error' : ''}`}
                                placeholder="9876543210"
                            />
                            {errors.contact && touched.contact && <p className="error-message">{errors.contact}</p>}
                        </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Enter your mobile number (without country code)</p>
                </div>
            </div>
            
            {/* Registration Summary */}
            <div className="mt-8 p-6 bg-gradient-to-r from-red-50 to-blue-50 rounded-xl border border-red-200">
                <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center">
                    <FaCheck className="text-green-600 mr-2" />
                    Registration Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="space-y-2">
                        <p><strong>Name:</strong> {formDataName}</p>
                        <p><strong>DOB:</strong> {formDataDob}</p>
                        <p><strong>Gender:</strong> {formDataGender}</p>
                    </div>
                    <div className="space-y-2">
                        <p><strong>Caste:</strong> {formDataCaste}</p>
                        <p><strong>Gotra:</strong> {formDataGotra}</p>
                        <p><strong>Marital Status:</strong> {formDataMarital}</p>
                    </div>
                    <div className="space-y-2">
                        <p><strong>Amount:</strong> Rs 4,000</p>
                        <p><strong>Country:</strong> {getSelectedCountryName() || 'Not selected'}</p>
                        <p><strong>State:</strong> {getSelectedStateName() || 'Not selected'}</p>
                        <p><strong>City:</strong> {localData.city || 'Not selected'}</p>
                        {localData.email && <p><strong>Email:</strong> {localData.email}</p>}
                        {localData.contact && localData.phone_code && (
                            <p><strong>Mobile:</strong> {localData.phone_code}{localData.contact}</p>
                        )}
                    </div>
                </div>
            </div>
            
            <div className="flex justify-between pt-4">
                <button
                    type="button"
                    onClick={onPrevious}
                    disabled={loading}
                    className="flex items-center space-x-2 px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-300"
                >
                    <FaArrowLeft />
                    <span>Previous</span>
                </button>
                <button
                    type="submit"
                    disabled={loading}
                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-red-600 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all duration-300 disabled:opacity-50"
                >
                    {loading ? (
                        <>
                            <div className="w-5 h-5 border-t-2 border-white rounded-full animate-spin"></div>
                            <span>Saving...</span>
                        </>
                    ) : (
                        <>
                            <span>Next: Career Information</span>
                            <span>→</span>
                        </>
                    )}
                </button>
            </div>
        </form>
    );
};

export default ContactInformation;